// src/components/dashboard/RiskScoreDial.jsx
// Custom SVG semi-circular dial showing portfolio risk score (0–100).
// Animated arc on load. No third-party chart library.

import { useEffect, useRef, useState } from 'react'

const RADIUS  = 80
const STROKE  = 12
const CENTER  = 100
const VIEW    = CENTER * 2   // 200

// Convert a score (0–100) to a position along the 180° arc
function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  }
}

function arcPath(cx, cy, r, startAngle, endAngle) {
  const s = polarToCartesian(cx, cy, r, endAngle)
  const e = polarToCartesian(cx, cy, r, startAngle)
  const large = endAngle - startAngle > 180 ? 1 : 0
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 0 ${e.x} ${e.y}`
}

function scoreToAngle(score) {
  // Arc spans 180° (left = 180°, right = 360°/0°)
  return 180 + (score / 100) * 180
}

function scoreToColor(score) {
  if (score <= 30) return '#3ddc84'
  if (score <= 55) return '#f5c518'
  if (score <= 75) return '#ff9500'
  return '#ff6b35'
}

export default function RiskScoreDial({ score, previousScore, size = 200 }) {
  const [animatedScore, setAnimatedScore] = useState(0)
  const frameRef = useRef(null)
  const startTimeRef = useRef(null)
  const duration = 1200

  useEffect(() => {
    if (score == null) return
    startTimeRef.current = null

    function tick(ts) {
      if (!startTimeRef.current) startTimeRef.current = ts
      const elapsed = ts - startTimeRef.current
      const progress = Math.min(elapsed / duration, 1)
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      setAnimatedScore(Math.round(eased * score))
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick)
      }
    }

    frameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameRef.current)
  }, [score])

  const displayScore = score ?? 0
  const color = scoreToColor(displayScore)
  const delta = previousScore != null ? displayScore - previousScore : null

  // Scale the 200-unit viewBox to the desired pixel size
  const scale = size / VIEW

  // Background arc: full 180° from 180° to 360°
  const bgArcPath = arcPath(CENTER, CENTER, RADIUS, 180, 360)

  // Animated fill arc
  const fillAngle = scoreToAngle(animatedScore)
  const fillArcPath = animatedScore > 0
    ? arcPath(CENTER, CENTER, RADIUS, 180, Math.min(fillAngle, 360))
    : null

  // Band label
  const bandLabel = displayScore <= 30 ? 'Low Risk'
    : displayScore <= 55 ? 'Moderate'
    : displayScore <= 75 ? 'Elevated'
    : 'High Risk'

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
      <svg
        width={size}
        height={size * 0.6}
        viewBox={`0 60 ${VIEW} ${VIEW * 0.6}`}
        style={{ overflow: 'visible' }}
      >
        {/* Background track */}
        <path
          d={bgArcPath}
          fill="none"
          stroke="#1a2332"
          strokeWidth={STROKE}
          strokeLinecap="round"
        />

        {/* Colored fill arc */}
        {fillArcPath && (
          <path
            d={fillArcPath}
            fill="none"
            stroke={color}
            strokeWidth={STROKE}
            strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 6px ${color}60)` }}
          />
        )}

        {/* Score number */}
        <text
          x={CENTER}
          y={CENTER + 8}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={color}
          fontSize={38}
          fontFamily="var(--font-mono, 'IBM Plex Mono', 'Courier New', monospace)"
          fontWeight="700"
          style={{ letterSpacing: '-1px' }}
        >
          {score == null ? '—' : animatedScore}
        </text>

        {/* Delta indicator */}
        {delta !== null && delta !== 0 && (
          <text
            x={CENTER}
            y={CENTER - 26}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={delta > 0 ? '#ff6b35' : '#3ddc84'}
            fontSize={11}
            fontFamily="var(--font-mono, 'IBM Plex Mono', 'Courier New', monospace)"
            fontWeight="600"
          >
            {delta > 0 ? '↑' : '↓'} {Math.abs(delta)} from yesterday
          </text>
        )}

        {/* Arc endpoint dot */}
        {fillArcPath && animatedScore > 0 && animatedScore < 100 && (() => {
          const pt = polarToCartesian(CENTER, CENTER, RADIUS, Math.min(fillAngle, 360))
          return (
            <circle
              cx={pt.x}
              cy={pt.y}
              r={STROKE / 2}
              fill={color}
              style={{ filter: `drop-shadow(0 0 4px ${color})` }}
            />
          )
        })()}

        {/* Low / High labels at arc ends */}
        <text x={18} y={CENTER + 6} fill="#3b4a5a" fontSize={10}
          fontFamily="var(--font-mono, 'IBM Plex Mono', 'Courier New', monospace)">LOW</text>
        <text x={174} y={CENTER + 6} fill="#3b4a5a" fontSize={10}
          fontFamily="var(--font-mono, 'IBM Plex Mono', 'Courier New', monospace)">HIGH</text>
      </svg>

      {/* Band label below */}
      <div style={{
        fontFamily: "var(--font-mono, 'IBM Plex Mono', 'Courier New', monospace)",
        fontSize: 11,
        color: color,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
        opacity: score == null ? 0 : 1,
        marginTop: -4,
      }}>
        {bandLabel}
      </div>
    </div>
  )
}
